import { FC } from 'react';
import { SummaryStatCard } from 'components/summary-stat-card/summary-stat-card';
import { formatNumber } from 'utils/number';
import type { DifferentialScenarioAggregations } from './types';
import styles from './summary-cards.module.scss';

export type SummaryCardType = 'last24h' | 'last7d' | 'lastMonth';

interface DifferentialSummaryCardsProps {
  aggregations: DifferentialScenarioAggregations | undefined;
  onCardClick?: (card: SummaryCardType) => void;
}

const formatTotal = (value: number | undefined) =>
  formatNumber(value, 0, 0, 'decimal', true, '--');

const formatDelta = (value: number | undefined) => {
  if (value === undefined) return '--';
  return `+${formatTotal(value)}`;
};

/**
 * Summary KPI tiles for the Differential Scenarios list page.
 * @param {DifferentialSummaryCardsProps} props - The component props
 * @return {JSX.Element} The summary cards grid
 */
const DifferentialSummaryCards: FC<DifferentialSummaryCardsProps> = ({
  aggregations,
  onCardClick,
}) => (
  <div className={styles.cardsContainer}>
    <div className={styles.cardCell}>
      <SummaryStatCard
        title="Total Scenarios"
        value={formatTotal(aggregations?.totalCount)}
      />
    </div>

    <div className={styles.cardCell}>
      <SummaryStatCard
        title="Created in Last 24 Hours"
        value={formatDelta(aggregations?.createdLast24h)}
        valueColor="green"
        tooltipText="Scenarios created in the 24 hours preceding now"
        onFilterClick={onCardClick ? () => onCardClick('last24h') : undefined}
        filterTooltip="Filter by Created in Last 24 Hours"
      />
    </div>

    <div className={styles.cardCell}>
      <SummaryStatCard
        title="Created in Last 7 Days"
        value={formatDelta(aggregations?.createdLast7d)}
        valueColor="green"
        tooltipText="Scenarios created in the 7 days preceding now"
        onFilterClick={onCardClick ? () => onCardClick('last7d') : undefined}
        filterTooltip="Filter by Created in Last 7 Days"
      />
    </div>

    <div className={styles.cardCell}>
      <SummaryStatCard
        title="Created in Last Month"
        value={formatDelta(aggregations?.createdLastMonth)}
        valueColor="green"
        tooltipText="Scenarios created in the 30 days preceding now"
        onFilterClick={onCardClick ? () => onCardClick('lastMonth') : undefined}
        filterTooltip="Filter by Created in Last Month"
      />
    </div>
  </div>
);

export { DifferentialSummaryCards };
